import React, { useState, useCallback, useEffect } from 'react';
import { __resolve } from '@/lib/i18n';
import { ChevronRight, List } from 'lucide-react';
import type { ThemeFormValues } from './ThemeEditorPanel';
import {
  ColorPaletteSection,
  ButtonsSection,
  TableActionButtonsSection,
  FormElementsSection,
  DisplaySection,
  StatCardsSection,
  CardSection,
  ListingBlocksSection,
  QuickActionsSection,
  PageHeadersSection,
  FeedbackSection,
  NavigationSection,
  StepsSection,
  LocationAutocompleteSection,
  CalendarSection,
  OverlaysSection,
  TableSection,
  LayoutSection,
  TreeViewSection,
  DateTimeSection,
  ThemeSettingsSection,
} from './sections';

interface ComponentShowcaseProps {
  className?: string;
  hideNav?: boolean;
  /** Theme form for inline editing (only in platform design mode) */
  form?: ThemeFormValues;
  onChange?: <K extends keyof ThemeFormValues>(key: K, value: ThemeFormValues[K]) => void;
  platformDefaults?: Partial<ThemeFormValues> | null;
}

interface SectionGroup {
  id: string;
  label: string;
  sections: { id: string; label: string; Component: React.ComponentType<any> }[];
}

function getSectionGroups(editable: boolean): SectionGroup[] {
  return [
    {
      id: 'foundation',
      label: 'Foundation',
      sections: [
        { id: 'color-palette', label: __resolve('ui::Color Palette'), Component: ColorPaletteSection },
        ...(editable ? [{ id: 'theme-settings', label: __resolve('ui::Theme Settings'), Component: ThemeSettingsSection }] : []),
        { id: 'layout', label: __resolve('ui::Layout'), Component: LayoutSection },
      ],
    },
    {
      id: 'inputs',
      label: 'Inputs',
      sections: [
        { id: 'buttons', label: __resolve('ui::Buttons'), Component: ButtonsSection },
        { id: 'form-elements', label: __resolve('ui::Form Elements'), Component: FormElementsSection },
        { id: 'date-time', label: __resolve('ui::Date & Time Pickers'), Component: DateTimeSection },
        { id: 'location-autocomplete', label: __resolve('ui::Location Autocomplete'), Component: LocationAutocompleteSection },
      ],
    },
    {
      id: 'data-display',
      label: 'Data Display',
      sections: [
        { id: 'display', label: __resolve('ui::Display Components'), Component: DisplaySection },
        { id: 'stat-cards', label: __resolve('ui::Stat Cards'), Component: StatCardsSection },
        { id: 'cards', label: __resolve('ui::Card Components'), Component: CardSection },
        { id: 'listing-blocks', label: __resolve('ui::Listing Blocks'), Component: ListingBlocksSection },
        { id: 'data-tables', label: __resolve('ui::Data Tables'), Component: TableSection },
        { id: 'tree-view', label: __resolve('ui::Tree View'), Component: TreeViewSection },
      ],
    },
    {
      id: 'navigation',
      label: 'Navigation',
      sections: [
        { id: 'page-headers', label: __resolve('ui::Page Headers'), Component: PageHeadersSection },
        { id: 'nav-components', label: __resolve('ui::Navigation'), Component: NavigationSection },
        { id: 'steps', label: __resolve('ui::Steps'), Component: StepsSection },
        { id: 'calendar', label: __resolve('ui::Calendar'), Component: CalendarSection },
      ],
    },
    {
      id: 'feedback-overlays',
      label: 'Feedback & Overlays',
      sections: [
        { id: 'feedback', label: __resolve('ui::Feedback'), Component: FeedbackSection },
        { id: 'overlays', label: __resolve('ui::Overlays'), Component: OverlaysSection },
      ],
    },
    {
      id: 'specialized',
      label: 'Specialized',
      sections: [
        { id: 'table-action-buttons', label: __resolve('ui::Table Action Buttons'), Component: TableActionButtonsSection },
        { id: 'quick-actions', label: __resolve('ui::Quick Actions'), Component: QuickActionsSection },
      ],
    },
  ];
}

export function ComponentShowcase({ className, hideNav, form, onChange, platformDefaults }: ComponentShowcaseProps) {
  const editable = !!form && !!onChange;
  const groups = getSectionGroups(editable);

  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const [activeSection, setActiveSection] = useState<string>('');
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const toggleGroup = useCallback((groupId: string) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  }, []);

  const collapseAll = useCallback(() => {
    setCollapsedGroups(new Set(groups.map(g => g.id)));
  }, [groups]);

  const expandAll = useCallback(() => {
    setCollapsedGroups(new Set());
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter(e => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length > 0) {
          setActiveSection(visible[0].target.id);
        }
      },
      { rootMargin: '-100px 0px -60% 0px', threshold: 0 }
    );

    const allSections = groups.flatMap(g => g.sections);
    allSections.forEach(s => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [groups]);

  return (
    <div className={`bg-background px-4 py-6 sm:px-6 lg:px-8 ${className ?? ''}`}>
      <div className="mx-auto max-w-[1400px]">
        {/* Developer info */}
        <div className="mb-8 p-4 rounded-lg bg-page-bg border border-border">
          <h3 className="text-sm font-semibold mb-1">For Developers</h3>
          <p className="text-sm text-muted-foreground">
            All components below are available via <code className="text-xs bg-muted px-1.5 py-0.5 rounded font-mono">@/components/ui</code>. Each section includes usage examples with code snippets you can copy directly. Components inherit the active theme tokens, so any customizations made here are reflected automatically.
          </p>
        </div>

        <div className="flex gap-8">
          {/* Sidebar */}
          {!hideNav && (
            <nav className="hidden lg:block w-56 shrink-0">
              <div className="sticky top-8 max-h-[calc(100vh-6rem)] overflow-y-auto overflow-x-hidden [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    Sections
                  </h2>
                  <button
                    type="button"
                    onClick={collapsedGroups.size === groups.length ? expandAll : collapseAll}
                    className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {collapsedGroups.size === groups.length ? 'Expand all' : 'Collapse all'}
                  </button>
                </div>
                <div className="space-y-2">
                  {groups.map(group => (
                    <div key={group.id}>
                      <button
                        type="button"
                        onClick={() => toggleGroup(group.id)}
                        className="flex items-center gap-1.5 w-full text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider py-1.5 px-2 rounded hover:bg-muted transition-colors"
                      >
                        <ChevronRight
                          className={`w-3 h-3 shrink-0 transition-transform duration-200 ${
                            !collapsedGroups.has(group.id) ? 'rotate-90' : ''
                          }`}
                        />
                        {group.label}
                      </button>
                      {!collapsedGroups.has(group.id) && (
                        <ul className="ml-3 mt-0.5 space-y-0.5">
                          {group.sections.map(({ id, label }) => (
                            <li key={id}>
                              <a
                                href={`#${id}`}
                                className={`block text-sm py-1 px-2 rounded transition-colors ${
                                  activeSection === id
                                    ? 'text-primary-600 bg-primary-50 font-medium'
                                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                                }`}
                              >
                                {label}
                              </a>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </nav>
          )}

          {/* Main content */}
          <div className="flex-1 min-w-0">
            {groups.map((group, groupIdx) => (
              <div key={group.id} className={groupIdx > 0 ? 'mt-16' : ''}>
                {/* Group header with divider */}
                <div className="flex items-center gap-3 mb-8">
                  <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-widest whitespace-nowrap">
                    {group.label}
                  </h2>
                  <div className="h-px flex-1 bg-border" />
                </div>
                {/* Sections within group */}
                <div className="space-y-12">
                  {group.sections.map(({ id, Component }) => (
                    <div key={id} id={id} className="scroll-mt-24">
                      {id === 'color-palette' ? (
                        <Component form={form} onChange={onChange} platformDefaults={platformDefaults} />
                      ) : id === 'theme-settings' ? (
                        <Component form={form} onChange={onChange} platformDefaults={platformDefaults} />
                      ) : (
                        <Component />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Mobile jump-to nav */}
      {!hideNav && <div className="lg:hidden fixed bottom-6 right-6 z-30">
        <div className="relative">
          <button
            type="button"
            onClick={() => setMobileNavOpen(!mobileNavOpen)}
            className="flex items-center gap-2 px-4 py-2.5 bg-primary-600 text-white rounded-full shadow-lg hover:bg-primary-700 transition-colors"
          >
            <List className="w-4 h-4" />
            <span className="text-sm font-medium">Jump to</span>
          </button>
          {mobileNavOpen && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setMobileNavOpen(false)} />
              <div className="absolute bottom-full right-0 mb-2 w-64 max-h-80 overflow-y-auto overflow-x-hidden [scrollbar-width:thin] bg-card border border-border rounded-lg shadow-xl z-50 py-2">
                {groups.map(group => (
                  <div key={group.id}>
                    <div className="px-3 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                      {group.label}
                    </div>
                    {group.sections.map(({ id, label }) => (
                      <a
                        key={id}
                        href={`#${id}`}
                        onClick={() => setMobileNavOpen(false)}
                        className="block px-4 py-1.5 text-sm text-foreground hover:bg-muted transition-colors"
                      >
                        {label}
                      </a>
                    ))}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>}
    </div>
  );
}
